import { useEffect, useState } from 'react';
import { KeyRound, LayoutGrid, LogIn } from 'lucide-react';
import { loginErrorText, passkeysSupported, signInWithOidc, signInWithPasskey } from '../lib/sso';
import { completeSetup, requestPasswordReset, resetPassword, signIn, signUp, usePublicConfig } from '../lib/auth';
import { navigate } from '../lib/router';
import { Button } from './ui/ui';
import { PasswordInput } from './ui/PasswordInput';

export const input =
  'min-h-12 w-full rounded-xl border border-[var(--line)] bg-[var(--panel)] px-3 text-base outline-none focus:border-accent';

export function translate(msg: string) {
  if (/invalid email or password|invalid password|credentials/i.test(msg)) return 'E-Mail oder Passwort stimmen nicht.';
  if (/already exists|user already/i.test(msg)) return 'Für diese E-Mail gibt es schon ein Konto. Melde dich an oder setze das Passwort zurück.';
  if (/password.*short|too short/i.test(msg)) return 'Das Passwort ist zu kurz (mindestens 8 Zeichen).';
  if (/too many/i.test(msg)) return 'Zu viele Versuche. Bitte warte kurz.';
  return msg;
}

import { BrandLogo } from './BrandLogo';
import { useAccentScope } from '../lib/theme';
import type { EntryBrand } from '@kaba/shared';

export function Shell({ children, title, brand }: { children: React.ReactNode; title?: string; brand?: EntryBrand | null }) {
  const { data: cfg } = usePublicConfig();
  // Einstieg über einen geprüften Link: Logo und Farbe des Boards statt der Installation (S01)
  useAccentScope(brand ? brand.accent : undefined, !!brand);
  const logo = brand?.logo ?? cfg?.branding?.logo;
  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <div className="flex w-full max-w-sm flex-col gap-3">
        <div className="mb-4 flex items-center gap-2">
          {logo ? (
            <BrandLogo logo={logo} name={brand?.logo ? 'Logo des Boards' : `Logo von ${cfg?.instanceName ?? 'KaBa'}`} size="preview-desktop" />
          ) : (
            <span className="inline-flex size-11 items-center justify-center rounded-2xl bg-accent text-on-accent">
              <LayoutGrid size={24} />
            </span>
          )}
          <div>
            <h1 className="text-2xl font-bold">{cfg?.instanceName ?? 'KaBa'}</h1>
            <p className="text-sm text-[var(--ink-soft)]">{title ?? 'Kanban für Teams'}</p>
          </div>
        </div>
        {children}
      </div>
    </main>
  );
}

export function LoginPage() {
  const { data: cfg } = usePublicConfig();
  if (cfg?.needsSetup) return <SetupPage />;
  return <LoginForm />;
}

/** Ersteinrichtung: Admin-Konto mit Einrichtungsschlüssel anlegen */
function SetupPage() {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Shell title="Einrichtung">
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setBusy(true);
          try {
            await completeSetup({ key, name, email, password });
          } catch (err) {
            setError(translate((err as Error).message));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-[var(--ink-soft)]">
          Lege das Admin-Konto an. Den Einrichtungsschlüssel findest du im Log des KaBa-Containers
          (oder in der Variable <code>KABA_SETUP_KEY</code>, falls gesetzt).
        </p>
        <PasswordInput
          className={input}
          placeholder="Einrichtungsschlüssel"
          aria-label="Einrichtungsschlüssel"
          autoComplete="off"
          required
          value={key}
          onChange={(e) => setKey(e.target.value)}
        />
        <input className={input} placeholder="Name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        <input className={input} type="email" placeholder="E-Mail" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <PasswordInput
          className={input}
          placeholder="Passwort"
          autoComplete="new-password"
          minLength={8}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy} className="min-h-12">
          Admin-Konto anlegen
        </Button>
      </form>
    </Shell>
  );
}

function LoginForm() {
  const params = new URLSearchParams(location.search);
  const { data: cfg } = usePublicConfig();
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(() => loginErrorText(params.get('login_error')));
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const login = cfg?.login;
  const [showPassword, setShowPassword] = useState(() => params.has('passwort'));
  const passwordVisible = mode !== 'login' || !login || login.passwordLogin || showPassword;
  const passkeys = !!login?.passkeys && passkeysSupported();

  // Fehlerparameter aus der Adresse entfernen, damit er beim Neuladen nicht wieder erscheint
  useEffect(() => {
    if (params.has('login_error')) history.replaceState(null, '', '/');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Passkey-Vorschläge direkt im E-Mail-Feld (wo der Browser das kann)
  useEffect(() => {
    if (!passkeys || mode !== 'login') return;
    signInWithPasskey(true).catch((err) => setError((err as Error).message));
    // (Fehler bei der eigentlichen Prüfung werden gemeldet; Abbruch bleibt still)
  }, [passkeys, mode]);

  const quick = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setError(translate((err as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const switchTo = (m: typeof mode) => {
    setMode(m);
    setError(null);
    setInfo(null);
  };

  return (
    <Shell title={mode === 'forgot' ? 'Passwort zurücksetzen' : undefined}>
      {mode === 'login' && (login?.oidc || passkeys) && (
        <div className="flex flex-col gap-2">
          {login?.oidc && (
            <Button variant="primary" className="min-h-12" disabled={busy} onClick={() => quick(() => signInWithOidc(location.pathname === '/app-verbinden' ? location.pathname + location.search : '/'))}>
              <LogIn size={18} /> {login.oidc.label}
            </Button>
          )}
          {passkeys && (
            <Button className="min-h-12" disabled={busy} onClick={() => quick(() => signInWithPasskey(false))}>
              <KeyRound size={18} /> Mit Passkey anmelden
            </Button>
          )}
          {passwordVisible && (
            <div className="my-1 flex items-center gap-3 text-xs text-[var(--ink-soft)]" aria-hidden>
              <span className="h-px flex-1 bg-[var(--line)]" /> oder mit Passwort <span className="h-px flex-1 bg-[var(--line)]" />
            </div>
          )}
        </div>
      )}
      {!passwordVisible && (
        <>
          {error && (
            <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
              {error}
            </p>
          )}
          <button type="button" className="min-h-11 text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]" onClick={() => setShowPassword(true)}>
            Mit Passwort anmelden
          </button>
        </>
      )}
      <form
        style={passwordVisible ? undefined : { display: 'none' }}
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setBusy(true);
          try {
            if (mode === 'login') await signIn(email, password);
            else if (mode === 'register') await signUp(name, email, password);
            else {
              await requestPasswordReset(email);
              setInfo('Falls es ein Konto mit dieser E-Mail gibt, ist ein Link zum Zurücksetzen unterwegs. Schau auch im Spam-Ordner nach.');
            }
          } catch (err) {
            setError(translate((err as Error).message));
          } finally {
            setBusy(false);
          }
        }}
      >
        {mode === 'register' && (
          <input className={input} placeholder="Name" autoComplete="name" required value={name} onChange={(e) => setName(e.target.value)} />
        )}
        <input
          className={input}
          type="email"
          placeholder="E-Mail"
          autoComplete={passkeys && mode === 'login' ? 'username webauthn' : 'email'}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {mode !== 'forgot' && (
          <PasswordInput
            className={input}
            placeholder="Passwort"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
            {error}
          </p>
        )}
        {info && (
          <p role="status" className="rounded-lg bg-emerald-50 p-2.5 text-sm text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
            {info}
          </p>
        )}
        <Button type="submit" variant="primary" disabled={busy} className="min-h-12">
          {mode === 'login' ? 'Anmelden' : mode === 'register' ? 'Konto anlegen' : 'Link anfordern'}
        </Button>
        {mode === 'login' && cfg?.mailEnabled && (
          <button type="button" className="min-h-10 text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]" onClick={() => switchTo('forgot')}>
            Passwort vergessen?
          </button>
        )}
        {(cfg?.signup === 'open' || mode !== 'login') && (
          <button
            type="button"
            className="min-h-11 text-sm text-[var(--ink-soft)] hover:text-[var(--ink)]"
            onClick={() => switchTo(mode === 'login' ? 'register' : 'login')}
          >
            {mode === 'login' ? 'Noch kein Konto? Registrieren' : 'Zurück zur Anmeldung'}
          </button>
        )}
        {mode === 'login' && cfg?.signup === 'invite' && (
          <p className="text-center text-xs text-[var(--ink-soft)]">Noch kein Konto? Lass dich zu einem Board einladen – du bekommst dann einen Link.</p>
        )}
      </form>
    </Shell>
  );
}

/** Ziel des Links aus der E-Mail: /reset-password?token=… */
export function ResetPasswordPage() {
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const invalid = params.get('error') === 'INVALID_TOKEN' || !token;
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (invalid)
    return (
      <Shell title="Passwort zurücksetzen">
        <p className="text-sm">Der Link ist ungültig oder abgelaufen. Fordere bitte einen neuen an.</p>
        <Button variant="primary" onClick={() => navigate('/')}>
          Zur Anmeldung
        </Button>
      </Shell>
    );

  return (
    <Shell title="Neues Passwort festlegen">
      {done ? (
        <>
          <p role="status" className="text-sm">
            Dein Passwort wurde geändert. Du kannst dich jetzt anmelden.
          </p>
          <Button variant="primary" onClick={() => navigate('/', { replace: true })}>
            Zur Anmeldung
          </Button>
        </>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (pw !== pw2) return setError('Die Passwörter stimmen nicht überein.');
            try {
              await resetPassword(token!, pw);
              setDone(true);
            } catch (err) {
              setError(translate((err as Error).message));
            }
          }}
        >
          <PasswordInput className={input} placeholder="Neues Passwort" autoComplete="new-password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} />
          <PasswordInput className={input} placeholder="Passwort wiederholen" autoComplete="new-password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} />
          {error && (
            <p role="alert" className="rounded-lg bg-rose-50 p-2.5 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
              {error}
            </p>
          )}
          <Button type="submit" variant="primary" className="min-h-12">
            Passwort speichern
          </Button>
        </form>
      )}
    </Shell>
  );
}
